import { apiClient } from '@/lib/api-client';
import { getActiveContext } from '@/lib/active-context-store';
import type { components, operations } from '@/lib/api-types-expenses-purpose-management';
export type Purpose = components['schemas']['Purpose'];
export type NoteType = components['schemas']['NoteType'];
export type Category = components['schemas']['Category'];
export type HistoryPage = components['schemas']['HistoryPage'];
export type PurposePage = components['schemas']['PurposePage'];
export type PurposeInput = components['schemas']['PurposeCreate'];
export type PurposePatch = components['schemas']['PurposePatch'];
export type ListQuery = NonNullable<operations['listPurposes']['parameters']['query']>;
export type Outcome<T> = { ok: true; value: T } | { ok: false; code: string };
const header = () => ({
  'X-Active-Tenant-Id': getActiveContext()?.tenantId ?? '',
  'X-Active-Organization-Id': getActiveContext()?.organizationId ?? '',
});
async function call<T>(
  run: () => Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<Outcome<T>> {
  try {
    const { data, error, response } = await run();
    if (response.ok && data !== undefined) return { ok: true, value: data };
    const code =
      typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
    return { ok: false, code: code || (response.status === 403 ? 'FORBIDDEN' : 'OTHER') };
  } catch {
    return { ok: false, code: 'OTHER' };
  }
}
export const listPurposes = (query: ListQuery) =>
  call(() =>
    apiClient.GET('/v1/expenses/purposes/manage', { params: { header: header(), query } }),
  );
export const getPurpose = (id: string) =>
  call(() =>
    apiClient.GET('/v1/expenses/purposes/manage/{id}', {
      params: { header: header(), path: { id } },
    }),
  );
export const createPurpose = (body: PurposeInput) =>
  call(() =>
    apiClient.POST('/v1/expenses/purposes/manage', { params: { header: header() }, body }),
  );
export const patchPurpose = (id: string, body: PurposePatch) =>
  call(() =>
    apiClient.PATCH('/v1/expenses/purposes/manage/{id}', {
      params: { header: header(), path: { id } },
      body,
    }),
  );
export const listNoteTypes = () =>
  call(() => apiClient.GET('/v1/expenses/note-types/manage', { params: { header: header() } }));
export const setCategories = (
  noteType: NoteType['noteType'],
  revision: number,
  categoryCodes: string[],
) =>
  call(() =>
    apiClient.PUT('/v1/expenses/note-types/{noteType}/categories', {
      params: { header: header(), path: { noteType } },
      body: { revision, categoryCodes },
    }),
  );
export const purposeHistory = (id: string, page: number) =>
  call(() =>
    apiClient.GET('/v1/expenses/purposes/manage/{id}/history', {
      params: { header: header(), path: { id }, query: { page, pageSize: 10 } },
    }),
  );
export const typeHistory = (noteType: NoteType['noteType'], page: number) =>
  call(() =>
    apiClient.GET('/v1/expenses/note-types/{noteType}/history', {
      params: { header: header(), path: { noteType }, query: { page, pageSize: 10 } },
    }),
  );
